// Flow-takenlijst met subtaken en snel toevoegen (planning-gauntlet stap 4, 25 sep 2026).
//
// Rust: een subtaak is een ingesprongen, randloze regel onder zijn ouder; de ouder toont
// alleen een stille teller "1/3" die in- en uitklapt. "+ subtaak" verschijnt pas bij
// aanwijzen van de regel; "+ taak toevoegen" is een grijze tekstregel onder de groep.
import { useState } from 'react';
import { Box, TextField } from '@mui/material';
import { T } from '../ProjectOverview/parts/stitch';
import { isTaakAfgerond } from '../ProjectOverview/parts/projectMeta';
import FaseTaakRij from './FaseTaakRij';

const stil = {
  border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', fontFamily: T.fontBody,
  fontSize: '0.78rem', color: T.outline, '&:hover': { color: T.text },
};

/**
 * Stille invoerregel: klik → veld, Enter = toevoegen (veld blijft open voor de volgende),
 * Esc of leeg wegklikken = stoppen.
 */
export function SnelRegel({ label = '+ taak toevoegen', ariaLabel, onVoegToe, ingesprongen = false, actief: actiefProp, onStop }) {
  const [eigenActief, setEigenActief] = useState(false);
  const actief = actiefProp ?? eigenActief;
  const [naam, setNaam] = useState('');
  const [bezig, setBezig] = useState(false);
  const stop = () => { setNaam(''); setEigenActief(false); onStop?.(); };
  const inspringing = ingesprongen ? { ml: 3.5, pl: 1.25, borderLeft: `1px solid ${T.surfaceHigh}` } : { px: 1.5 };

  if (!actief) {
    return (
      <Box component="button" type="button" onClick={() => setEigenActief(true)}
        sx={{ ...stil, ...inspringing, py: 0.5, textAlign: 'left', alignSelf: 'flex-start' }}>
        {label}
      </Box>
    );
  }
  const bewaar = async () => {
    const schoon = naam.trim();
    if (!schoon || bezig) return;
    setBezig(true);
    const gelukt = await onVoegToe(schoon);
    setBezig(false);
    if (gelukt) setNaam('');
  };
  return (
    <Box sx={{ ...inspringing, py: 0.25 }}>
      <TextField
        autoFocus fullWidth variant="standard" value={naam} disabled={bezig}
        placeholder="Taaknaam… (Enter)"
        inputProps={{ 'aria-label': ariaLabel }}
        InputProps={{ disableUnderline: true }}
        onChange={(e) => setNaam(e.target.value)}
        onBlur={() => { if (!naam.trim()) stop(); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); bewaar(); }
          if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); stop(); }
        }}
        sx={{ '& .MuiInputBase-input': { fontFamily: T.fontBody, fontSize: '0.82rem', py: 0.5 } }}
      />
    </Box>
  );
}

/** Eén taak met zijn subtaken eronder (inklapbaar), plus "+ subtaak". */
export function TaakMetSubtaken({ knoop, dicht, onWissel, onOpen, onToggleComplete, kanSub, onSubtaak }) {
  const { taak, kinderen } = knoop;
  const [invoer, setInvoer] = useState(false);
  const af = kinderen.filter((k) => isTaakAfgerond(k)).length;
  const naam = taak.fields?.NaamTaak || 'taak';

  const rechts = (
    <>
      {kinderen.length > 0 && (
        <Box component="button" type="button" aria-expanded={!dicht}
          aria-label={`${dicht ? 'Toon' : 'Verberg'} subtaken van ${naam}`}
          title={`${af} van ${kinderen.length} subtaken af`}
          onClick={(e) => { e.stopPropagation(); onWissel(); }}
          sx={stil}>
          {af}/{kinderen.length}
        </Box>
      )}
      {kanSub && (
        <Box component="button" type="button" className="fase-rij-actie"
          aria-label={`Subtaak toevoegen aan ${naam}`}
          onClick={(e) => { e.stopPropagation(); if (dicht) onWissel(); setInvoer(true); }}
          sx={{ ...stil, opacity: invoer ? 1 : 0, transition: 'opacity 0.1s ease', '&:focus-visible': { opacity: 1 } }}>
          + subtaak
        </Box>
      )}
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <FaseTaakRij task={taak} onClick={() => onOpen(taak)} onToggleComplete={onToggleComplete} rechts={rechts} />
      {!dicht && (kinderen.length > 0 || (kanSub && invoer)) && (
        <Box role="list" aria-label={`Subtaken van ${naam}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {kinderen.map((k) => (
            <FaseTaakRij key={k.id} task={k} ingesprongen onClick={() => onOpen(k)} onToggleComplete={onToggleComplete} />
          ))}
          {kanSub && invoer && (
            <SnelRegel ingesprongen actief ariaLabel={`Nieuwe subtaak onder ${naam}`}
              onVoegToe={onSubtaak} onStop={() => setInvoer(false)} />
          )}
        </Box>
      )}
    </Box>
  );
}
